import useWebSocket, { ReadyState } from 'react-use-websocket';
import * as configs from '@/configs';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAtomValue } from 'jotai';
import { isLoggedIn } from '@/state/userInfo';
import { COOKIE_KEY_USER_TOKEN } from '@/constants/cookieStorageKeys';
import Cookie from '@/utils/cookies';

import getDeviceUid from '@/helper/getDeviceUid';
import {
  IWebSocketData,
  WebSocketCategoryEnum,
  WebSocketCommandsEnum,
} from '@/types/WS';
import { getLocalStorage } from '@/utils/localStoreHelper';
import { LOCAL_KEY_USER_TOKEN } from '@/constants/localStorageKeys';
import { usePageVisibility } from '@/hooks/usePageVisibility';

const deviceUid = getDeviceUid();
const socketUrl = configs.env.endpoints.websocket;

export const useGlobalWebsocket = <T extends WebSocketCategoryEnum>({
  onMessage,
  category,
  command,
  shouldConnect: shouldReconnectProp,
}: {
  onMessage?: (data: { data: IWebSocketData[T] }) => void;
  category: T;
  command?: WebSocketCommandsEnum;
  shouldConnect?: boolean;
}) => {
  const isPageActive = usePageVisibility();
  const [deviceOrUserInfoRegistered, setDeviceOrUserInfoRegistered] =
    useState<boolean>(false);
  const wsConnectedPromiseRef = useRef<Promise<WebSocketEventMap['open']>>();
  const wsConnectedResolveRef =
    useRef<(ins: WebSocketEventMap['open']) => void>();
  const isLogin = useAtomValue(isLoggedIn);
  const shouldConnect = true;

  const {
    sendMessage,
    sendJsonMessage,
    readyState,
    lastJsonMessage,
    getWebSocket,
    lastMessage,
  } = useWebSocket(
    socketUrl,
    {
      share: true,
      //Will attempt to reconnect on all close events, such as server shutting down
      shouldReconnect: (closeEvent) => isPageActive,
      heartbeat: {
        message: 'ping',
        returnMessage: 'pong',
        timeout: 60000, // 1 minute, if no response is received, the connection will be closed
        interval: 25000, // every 25 seconds, a ping message will be sent
      },
      filter: (message) => {
        if (message.data === 'pong') {
          return true;
        }
        const inComingMessage = JSON.parse(message.data);
        if (command === undefined && category === undefined) return true;
        return command === undefined
          ? inComingMessage.data?.category === category
          : inComingMessage.data?.category === category &&
              inComingMessage.data?.command === command;
      },
      onOpen: (ins) => {
        if (wsConnectedResolveRef.current) {
          wsConnectedResolveRef.current(ins);
        }
      },
      onClose: () => {
        console.log('WebSocket close');
      },
      onError: (e) => {
        console.error('WebSocket error', e);
      },
      onMessage: ({ data }) => {
        try {
          if (data === 'pong' || !data) return;
          const metaData = JSON.parse(data);
          // reconnect
          if (metaData?.command === WebSocketCommandsEnum.RECONNECT) {
            if (!isLogin) {
              registerDeviceInfo();
              return;
            }

            if (isLogin) {
              registerUserInfo();
            }
          }

          if (category === undefined && command === undefined) {
            return onMessage?.({ data: metaData });
          }
          if (category !== undefined && command !== undefined) {
            return (
              metaData?.data?.category === category &&
              metaData?.data?.command === command &&
              onMessage?.({ data: metaData })
            );
          }
          if (
            metaData?.data?.category === category ||
            metaData?.data?.command === command
          ) {
            return onMessage?.({ data: metaData });
          }
        } catch (e) {
          console.error(
            `useGlobalWebsocket onMessage error, category ${category}`,
            e,
          );
        }
      },
    },
    // shouldReconnect: true,
    shouldConnect,
  );

  const advancedSendJsonMessage = (message?: any) => {
    if (readyState === ReadyState.OPEN) {
      sendJsonMessage({
        ...message,
      });
    }
  };

  const registerDeviceInfo = useCallback(() => {
    if (readyState !== ReadyState.OPEN) return;
    sendJsonMessage({
      deviceUid,
      command: WebSocketCommandsEnum.LOGIN_REQUEST,
    });
  }, [readyState, sendJsonMessage]);

  const registerUserInfo = useCallback(() => {
    if (readyState !== ReadyState.OPEN) return;
    const { cookie } = new Cookie();
    const token =
      cookie.get(COOKIE_KEY_USER_TOKEN) ||
      getLocalStorage(LOCAL_KEY_USER_TOKEN);
    sendJsonMessage({
      deviceUid,
      token: token,
      command: WebSocketCommandsEnum.LOGIN_REQUEST,
    });
  }, [readyState, sendJsonMessage]);

  const clearUserInfo = () => {
    if (readyState !== ReadyState.OPEN) return;
    sendJsonMessage({
      deviceUid,
      command: 1,
    });
    setDeviceOrUserInfoRegistered(false);
  };

  useEffect(() => {
    wsConnectedPromiseRef.current = new Promise<WebSocketEventMap['open']>(
      (resolve) => {
        wsConnectedResolveRef.current = resolve;
      },
    );
  }, []);

  useEffect(() => {
    if (readyState === ReadyState.CONNECTING) return;

    if (!isLogin) {
      registerDeviceInfo();
      return;
    }

    if (isLogin) {
      registerUserInfo();
    }
  }, [readyState, isLogin, registerDeviceInfo, registerUserInfo]);

  return {
    readyState,
    sendMessage,
    sendJsonMessage: advancedSendJsonMessage,
    lastMessage,
    lastJsonMessage,
    registerDeviceInfo,
    registerUserInfo,
  };
};

export default useGlobalWebsocket;
